import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { Text } from 'components/Text';
import { SearchBox } from 'components/Search';
import { Pagination } from 'components/Pagination';
import { LoadingScreen } from 'components/LoadingScreen';
import {
  GET_RETURN_METRICS,
  GET_RETURNS_ANALYTICS,
  RETURNS_DASHBOARD,
} from 'gql/queries';
import { withNotification } from 'components/Notification';
import {
  apiErrorHandler,
  getStoreCurrencyFormatter,
  NotificationTypes,
} from 'utils';
import { useStore } from 'context/store-context';
import { ReturnsTable } from './ReturnsTable';
import { columns } from './data/returns-data';
import {
  buildReturnAnalytics,
  buildReturnAnalyticsCsv,
} from './returns-analytics';
import {
  ReturnAnalyticsRecord,
  ReturnDashboardNode,
  ReturnMetricBucket,
  ReturnMetricCounts,
  ReturnMetricsAggregate,
  ReturnMetricsQueryResult,
  ReturnsAnalyticsQueryResult,
  ReturnsDashboardBucket,
  ReturnsDashboardBucketCount,
  ReturnsDashboardOrderBy,
} from 'types/return';

const RETURN_DASHBOARD_BUCKETS: Array<{
  value: ReturnsDashboardBucket;
  label: string;
}> = [
  { value: 'OPEN', label: 'Open' },
  { value: 'PENDING_REVIEW', label: 'Pending review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'IN_TRANSIT', label: 'In transit' },
  { value: 'DELIVERED_BACK', label: 'Delivered back' },
  { value: 'PENDING_REFUND', label: 'Pending refund' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'FLAGGED', label: 'Flagged' },
];

const DEFAULT_RETURNS_ORDER_BY: ReturnsDashboardOrderBy = 'CREATED_AT_DESC';

const RETURN_METRICS: Array<{
  bucket: ReturnMetricBucket;
  label: string;
  description: string;
}> = [
  {
    bucket: 'ALL',
    label: 'Total requests',
    description: 'All return and exchange requests',
  },
  {
    bucket: 'NEEDS_REVIEW',
    label: 'Needs review',
    description: 'Waiting for a team decision',
  },
  {
    bucket: 'AWAITING_SHIPMENT',
    label: 'Awaiting shipment',
    description: 'Approved, waiting for customer shipment',
  },
  {
    bucket: 'IN_TRANSIT',
    label: 'In transit',
    description: 'Moving back to your team',
  },
  {
    bucket: 'READY_TO_REFUND',
    label: 'Ready to refund',
    description: 'Received or due for settlement',
  },
  {
    bucket: 'COMPLETE',
    label: 'Complete',
    description: 'Resolved return and exchange workflows',
  },
];

const EMPTY_RETURN_METRIC_COUNTS: ReturnMetricCounts = {
  ALL: 0,
  NEEDS_REVIEW: 0,
  AWAITING_SHIPMENT: 0,
  IN_TRANSIT: 0,
  READY_TO_REFUND: 0,
  COMPLETE: 0,
};

const getAggregateCount = (value?: ReturnMetricsAggregate | null): number =>
  Number(value?.aggregate?.count ?? 0);

const buildReturnMetricCounts = (
  data?: ReturnMetricsQueryResult | null,
): ReturnMetricCounts => ({
  ALL: getAggregateCount(data?.totalReturns),
  NEEDS_REVIEW: getAggregateCount(data?.needsReview),
  AWAITING_SHIPMENT: getAggregateCount(data?.awaitingShipment),
  IN_TRANSIT: getAggregateCount(data?.inTransit),
  READY_TO_REFUND: getAggregateCount(data?.readyToRefund),
  COMPLETE: getAggregateCount(data?.complete),
});

const dateInputValue = (value: string) => value.slice(0, 10);

const startOfDayIso = (date: string) => `${date}T00:00:00.000Z`;

const endOfDayIso = (date: string) => `${date}T23:59:59.999Z`;

const formatDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const getDefaultReturnMetricsDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 30);

  return {
    startDate: startOfDayIso(formatDateInputValue(startDate)),
    endDate: endOfDayIso(formatDateInputValue(endDate)),
  };
};

export const Returns = withNotification(({ showNotification }: any) => {
  const limit = 20;
  const [allReturns, setAllReturns] = useState<ReturnDashboardNode[]>([]);
  const [buckets, setBuckets] = useState<ReturnsDashboardBucketCount[]>([]);
  const [returnMetricCounts, setReturnMetricCounts] =
    useState<ReturnMetricCounts>(EMPTY_RETURN_METRIC_COUNTS);
  const [returnAnalyticsRecords, setReturnAnalyticsRecords] = useState<
    ReturnAnalyticsRecord[]
  >([]);
  const [returnMetricsDateRange, setReturnMetricsDateRange] = useState(
    getDefaultReturnMetricsDateRange,
  );
  const [isReturnMetricsExpanded, setIsReturnMetricsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(0);
  const [activeBucket, setActiveBucket] =
    useState<ReturnsDashboardBucket>('OPEN');
  const [orderBy, setOrderBy] = useState<ReturnsDashboardOrderBy>(
    DEFAULT_RETURNS_ORDER_BY,
  );

  const navigate = useNavigate();
  const { storeId, storeProperties } = useStore();
  const normalizedSearch = searchValue.trim();

  const handleDashboardCompleted = (data: any) => {
    const result = data?.returnsDashboard || {};
    setAllReturns(result.nodes || []);
    setBuckets(result.buckets || []);
  };

  const [fetchReturnMetrics, { loading: metricsLoading }] = useLazyQuery(
    GET_RETURN_METRICS,
    {
      fetchPolicy: 'network-only',
      onCompleted(data: ReturnMetricsQueryResult) {
        setReturnMetricCounts(buildReturnMetricCounts(data));
      },
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    },
  );

  const [fetchReturnsAnalytics, { loading: analyticsLoading }] = useLazyQuery(
    GET_RETURNS_ANALYTICS,
    {
      fetchPolicy: 'network-only',
      onCompleted(data: ReturnsAnalyticsQueryResult) {
        setReturnAnalyticsRecords(data?.returns || []);
      },
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    },
  );

  const [fetchReturns, { loading }] = useLazyQuery(RETURNS_DASHBOARD, {
    fetchPolicy: 'network-only',
    onCompleted: handleDashboardCompleted,
    onError(error) {
      const newError = apiErrorHandler(error);
      showNotification(NotificationTypes.error, newError?.message);
    },
  });

  useEffect(() => {
    if (storeId) {
      fetchReturns({
        variables: {
          input: {
            storeId,
            bucket: activeBucket,
            search: normalizedSearch || undefined,
            limit,
            offset,
            orderBy: orderBy || 'CREATED_AT_DESC',
          },
        },
      });
    }
  }, [
    fetchReturns,
    storeId,
    activeBucket,
    normalizedSearch,
    limit,
    offset,
    orderBy,
  ]);

  useEffect(() => {
    if (storeId) {
      fetchReturnMetrics({
        variables: {
          storeId,
          startDate: returnMetricsDateRange.startDate,
          endDate: returnMetricsDateRange.endDate,
        },
      });
      fetchReturnsAnalytics({
        variables: {
          storeId,
          startDate: returnMetricsDateRange.startDate,
          endDate: returnMetricsDateRange.endDate,
        },
      });
    }
  }, [
    fetchReturnMetrics,
    fetchReturnsAnalytics,
    storeId,
    returnMetricsDateRange.startDate,
    returnMetricsDateRange.endDate,
  ]);

  const returnAnalytics = useMemo(
    () => buildReturnAnalytics(returnAnalyticsRecords),
    [returnAnalyticsRecords],
  );

  const returnMetrics = RETURN_METRICS.map((metric) => ({
    ...metric,
    count: returnMetricCounts[metric.bucket],
  }));
  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber);
    setOffset(limit * (pageNumber - 1));
  };

  const onViewReturn = (ret: ReturnDashboardNode) => {
    navigate(`/returns/${ret.id}`);
  };

  const getBucketCount = (bucket: ReturnsDashboardBucket) => {
    const match = buckets.find((item) => item.bucket === bucket);
    return Number(match?.count ?? 0);
  };

  const handleBucketChange = (bucket: ReturnsDashboardBucket) => {
    setActiveBucket(bucket);
    setPage(1);
    setOffset(0);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPage(1);
    setOffset(0);
  };

  const handleReturnMetricsStartDateChange = (value: string) => {
    if (!value) return;

    const nextStartDate = startOfDayIso(value);
    setReturnMetricsDateRange((current) => ({
      startDate: nextStartDate,
      endDate:
        new Date(nextStartDate) > new Date(current.endDate)
          ? endOfDayIso(value)
          : current.endDate,
    }));
  };

  const handleReturnMetricsEndDateChange = (value: string) => {
    if (!value) return;

    const nextEndDate = endOfDayIso(value);
    setReturnMetricsDateRange((current) => ({
      startDate:
        new Date(nextEndDate) < new Date(current.startDate)
          ? startOfDayIso(value)
          : current.startDate,
      endDate: nextEndDate,
    }));
  };

  const downloadReturnAnalyticsCsv = () => {
    if (!hasReturnAnalytics) return;

    const startDate = dateInputValue(returnMetricsDateRange.startDate);
    const endDate = dateInputValue(returnMetricsDateRange.endDate);
    const csv = buildReturnAnalyticsCsv(returnAnalytics, {
      startDate,
      endDate,
      currency,
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `shipaid-return-exchange-analytics-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSelectedColumn = (column: string, direction: string) => {
    if (column === 'updatedAt') {
      setOrderBy(direction === 'asc' ? 'UPDATED_AT_ASC' : 'UPDATED_AT_DESC');
      setPage(1);
      setOffset(0);
    }
  };

  const search = () => {
    // Search is handled by useEffect on searchValue
  };

  const returnsCount =
    buckets.length > 0 ? getBucketCount(activeBucket) : allReturns.length;
  const currency = storeProperties?.currency || 'USD';
  const formatMoney = (value: number) =>
    getStoreCurrencyFormatter(currency, value);
  const topReturnsByItem = returnAnalytics.returnsByItem.slice(0, 5);
  const topReasonsByItem = returnAnalytics.reasonsByItem.slice(0, 4);
  const topItemsByReason = returnAnalytics.itemsByReason.slice(0, 4);
  const topReturnReasons = returnAnalytics.returnReasons.slice(0, 6);
  const hasReturnAnalytics = returnAnalytics.summary.totalReturnItems > 0;

  if (loading && allReturns.length === 0) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="md:flex justify-between items-center mb-6">
        <Text value="All Returns & Exchanges" type="h4" />
      </div>

      <section className="mb-8">
        <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Text value="Return & exchange metrics" type="bold" />
            <p className="mt-1 text-sm text-gray-500">
              Operational counts from the return and exchange workflow queue,
              filtered by request creation date.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              <span className="mb-1 block">Start (UTC)</span>
              <input
                type="date"
                aria-label="Return and exchange metrics start date"
                value={dateInputValue(returnMetricsDateRange.startDate)}
                max={dateInputValue(returnMetricsDateRange.endDate)}
                onChange={(event) =>
                  handleReturnMetricsStartDateChange(event.target.value)
                }
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium normal-case tracking-normal text-gray-700"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              <span className="mb-1 block">End (UTC)</span>
              <input
                type="date"
                aria-label="Return and exchange metrics end date"
                value={dateInputValue(returnMetricsDateRange.endDate)}
                min={dateInputValue(returnMetricsDateRange.startDate)}
                onChange={(event) =>
                  handleReturnMetricsEndDateChange(event.target.value)
                }
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium normal-case tracking-normal text-gray-700"
              />
            </label>
            <button
              type="button"
              onClick={downloadReturnAnalyticsCsv}
              disabled={!hasReturnAnalytics}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Download CSV
            </button>
            {(metricsLoading || analyticsLoading) && (
              <p className="pb-2 text-sm font-medium text-gray-500">
                Refreshing
              </p>
            )}
          </div>
        </div>

        <p className="mb-4 text-xs text-gray-500">
          Includes return and exchange requests. Item value reflects original
          items, not refunds or replacement value. All date boundaries and CSV ranges use UTC.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {returnMetrics.map((metric) => (
            <div
              key={metric.bucket}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {metric.label}
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {metric.count}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-gray-500">
                {metric.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Original item value
            </p>
            <p className="mt-2 text-2xl font-bold text-gray-900">
              {formatMoney(returnAnalytics.summary.totalReturnedValue)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Merchandise value of original items in requests
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Carrier label costs
            </p>
            <p className="mt-2 text-2xl font-bold text-gray-900">
              {formatMoney(returnAnalytics.summary.totalLabelCost)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Carrier postage for generated return labels.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Estimated cost
            </p>
            <p className="mt-2 text-2xl font-bold text-gray-900">
              {formatMoney(returnAnalytics.summary.totalEstimatedCost)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Original item value plus carrier label costs
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Items per request
            </p>
            <p className="mt-2 text-2xl font-bold text-gray-900">
              {returnAnalytics.summary.averageItemsPerReturn}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              {returnAnalytics.summary.totalReturnItems} requested units
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Detailed product and reason analytics
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Expand to inspect item, request reason, and cost drivers for this
              date range.
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              setIsReturnMetricsExpanded((currentValue) => !currentValue)
            }
            className="rounded-md border border-blue-600 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
          >
            {isReturnMetricsExpanded
              ? 'Hide detailed analytics'
              : 'Show detailed analytics'}
          </button>
        </div>

        {isReturnMetricsExpanded && !hasReturnAnalytics && (
          <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
            No return or exchange item analytics available for this date range.
          </div>
        )}

        {isReturnMetricsExpanded && hasReturnAnalytics && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <Text value="Requests by item" type="bold" />
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <th className="pb-2 pr-4">Item</th>
                        <th className="pb-2 pr-4">Requests</th>
                        <th className="pb-2 pr-4">Units</th>
                        <th className="pb-2 pr-4">Value</th>
                        <th className="pb-2">Top reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topReturnsByItem.map((item) => (
                        <tr key={item.itemKey}>
                          <td className="py-3 pr-4">
                            <p className="font-medium text-gray-900">
                              {item.itemTitle}
                            </p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {item.returns}
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {item.quantity}
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {formatMoney(item.value)}
                          </td>
                          <td className="py-3 text-gray-700">
                            {item.topReason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <Text value="Request reasons" type="bold" />
                <div className="mt-4 space-y-3">
                  {topReturnReasons.map((reason) => (
                    <div
                      key={reason.reason}
                      className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3 last:border-0 last:pb-0"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {reason.reason}
                        </p>
                        <p className="text-xs text-gray-500">
                          {reason.quantity} units across {reason.returns}{' '}
                          requests
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatMoney(reason.value)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <Text value="Request reasons by item" type="bold" />
                <div className="mt-4 space-y-4">
                  {topReasonsByItem.map((item) => (
                    <div
                      key={item.itemKey}
                      className="border-b pb-4 last:border-0"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.itemTitle}
                          </p>
                          <p className="text-xs text-gray-500">{item.sku}</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">
                          {item.quantity} units
                        </p>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {item.reasons.slice(0, 4).map((reason) => (
                          <span
                            key={`${item.itemKey}-${reason.reason}`}
                            className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
                          >
                            {reason.reason}: {reason.quantity}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <Text value="Items by request reason" type="bold" />
                <div className="mt-4 space-y-4">
                  {topItemsByReason.map((reason) => (
                    <div
                      key={reason.reason}
                      className="border-b pb-4 last:border-0"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-medium text-gray-900">
                            {reason.reason}
                          </p>
                          <p className="text-xs text-gray-500">
                            {reason.quantity} units across {reason.returns}{' '}
                            requests
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatMoney(reason.value)}
                        </p>
                      </div>
                      <div className="mt-3 space-y-2">
                        {reason.items.slice(0, 3).map((item) => (
                          <div
                            key={`${reason.reason}-${item.itemKey}`}
                            className="flex items-center justify-between gap-3 text-sm"
                          >
                            <span className="truncate text-gray-700">
                              {item.itemTitle}
                            </span>
                            <span className="font-medium text-gray-900">
                              {item.quantity}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <Text value="Costs" type="bold" />
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Average returned value
                  </p>
                  <p className="mt-1 text-xl font-bold text-gray-900">
                    {formatMoney(returnAnalytics.costs.averageReturnedValue)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Average carrier label cost
                  </p>
                  <p className="mt-1 text-xl font-bold text-gray-900">
                    {formatMoney(returnAnalytics.costs.averageLabelCost)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Highest cost item
                  </p>
                  <p className="mt-1 text-xl font-bold text-gray-900">
                    {returnAnalytics.costs.highestCostItem?.itemTitle || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatMoney(
                      returnAnalytics.costs.highestCostItem?.value || 0,
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Highest cost reason
                  </p>
                  <p className="mt-1 text-xl font-bold text-gray-900">
                    {returnAnalytics.costs.highestCostReason?.reason || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatMoney(
                      returnAnalytics.costs.highestCostReason?.value || 0,
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="md:flex justify-between mb-2 mt-4">
        <div className="mb-4 flex items-center">
          <div className="w-[25rem]">
            <SearchBox
              search={search}
              searchValue={searchValue}
              onChange={handleSearch}
              placeholder="Search by request no, Order no"
            />
          </div>
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Return and exchange buckets"
        className="mb-4 flex flex-wrap gap-2"
      >
        {RETURN_DASHBOARD_BUCKETS.map(({ value, label }) => {
          const isActive = activeBucket === value;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => handleBucketChange(value)}
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${
                isActive
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span>{label}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  isActive
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {getBucketCount(value)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="">
        <ReturnsTable
          returns={allReturns.map((ret) => ({
            ...ret,
            onViewReturn: () => onViewReturn(ret),
            currency: storeProperties?.currency,
          }))}
          columns={columns}
          emptyMessage={
            searchValue
              ? `No search results matches "${searchValue}"`
              : 'No returns or exchanges found'
          }
          orderByOptions={['updatedAt']}
          handleSelectedColumn={handleSelectedColumn}
        />
      </div>

      <div className="mt-3">
        {returnsCount > limit && (
          <Pagination
            totalPages={Math.ceil(returnsCount / limit)}
            currentPage={page}
            resultsRange={limit}
            totalResults={returnsCount}
            onPageClick={onPageChange}
          />
        )}
      </div>
    </div>
  );
});
